import {
  AggregatedOptionStackLeaf,
  AggregatedOptionStackNode,
  MicrositeWidgetEntity,
} from '@atlas-metrics/microsite-types/dist/types/typeIndex';
import { useTranslation } from 'next-i18next';

import { FormatValueFn } from '@/hooks/useFormatValue';

import { getRowContent, isLeaf } from './utils';
import HorizontalDivider from '../ui/HorizontalDivider';
import WidgetRow from '../ui/WidgetRow';

type Props = {
  option: AggregatedOptionStackNode | AggregatedOptionStackLeaf;
  widget: MicrositeWidgetEntity;
  nodeKey: string;
  level?: 1 | 2 | 3;
  formatValue: FormatValueFn;
};

const StackWidget: React.FC<Props> = ({
  option,
  widget,
  nodeKey,
  level = 1,
  formatValue,
}) => {
  const { t } = useTranslation();
  const accordionText = {
    toOpenText: t('accordion.expand'),
    toCloseText: t('accordion.collapse'),
  };

  const displayLeaf = (
    leaf: AggregatedOptionStackLeaf,
    widget: MicrositeWidgetEntity,
    initialKey: string,
    level: 1 | 2 | 3
  ): JSX.Element => {
    const rowContent = getRowContent(widget, leaf.value, formatValue, t);
    const value =
      'value' in rowContent && rowContent.value != null
        ? rowContent.value
        : leaf.optionName;
    const title =
      !('value' in rowContent) || rowContent.value == null
        ? null
        : leaf.optionName;
    return (
      <div key={`${initialKey}_l-${leaf.optionId}`}>
        <HorizontalDivider className='' />
        <WidgetRow
          title={title}
          {...rowContent}
          value={value}
          depthLevel={level}
          accordion={accordionText}
        />
      </div>
    );
  };

  const displayNode = (
    node: AggregatedOptionStackNode,
    widget: MicrositeWidgetEntity,
    initialKey: string,
    level: 1 | 2 | 3
  ): JSX.Element => {
    const nodeKey = `${initialKey}_n-${node.optionId}`;

    return (
      <div key={nodeKey}>
        <HorizontalDivider className='' />
        <WidgetRow title={node.optionName} depthLevel={level} />
        {(node.stack.options ?? []).map((option, idx) => (
          <StackWidget
            key={idx}
            option={option}
            widget={widget}
            nodeKey={nodeKey}
            level={(level + 1) as 2 | 3}
            formatValue={formatValue}
          />
        ))}
      </div>
    );
  };

  return isLeaf(option)
    ? displayLeaf(option, widget, nodeKey, level)
    : displayNode(option, widget, nodeKey, level);
};

export default StackWidget;
